import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { createCheckoutSession } from '../../services/stripe';
import { useAuth } from '../../context/AuthContext';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  backdrop-filter: blur(5px);
  overflow-y: auto;
`;

const ModalContent = styled.div`
  background-color: #2c1249;
  border-radius: 20px;
  width: 100%;
  max-width: 800px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  position: relative;
  color: #ffffff;
  margin: 20px;
  
  @media (max-width: 768px) {
    padding: 20px;
    max-width: calc(100% - 30px);
    margin: 15px;
    max-height: calc(100vh - 30px);
  }
`;

const CloseButton = styled.span`
  position: absolute;
  top: 20px;
  right: 25px;
  color: rgba(255, 255, 255, 0.7);
  font-size: 28px;
  cursor: pointer;
  transition: color 0.3s ease;
  
  &:hover {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    top: 15px;
    right: 15px;
  }
`;

const ModalTitle = styled.h2`
  color: var(--primary-color);
  margin-bottom: 25px;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  font-size: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
    margin-bottom: 20px;
    padding-bottom: 10px;
    margin-right: 30px;
  }
`;

const PlansContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 20px;
  overflow: visible;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
    overflow: visible;
  }
`;

const PlanCard = styled.div`
  background: linear-gradient(135deg, rgba(40, 10, 70, 0.8) 0%, rgba(60, 15, 105, 0.8) 100%);
  border-radius: 15px;
  padding: 25px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 64, 192, 0.3);
  }
  
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, var(--primary-color), var(--secondary-color));
    z-index: 1;
  }
  
  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`;

const PlanTitle = styled.h3`
  color: var(--secondary-color);
  margin-bottom: 15px;
  font-size: 1.5rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin-bottom: 10px;
    margin-top: 5px;
  }
`;

const CreditsAmount = styled.div`
  color: white;
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  i {
    color: var(--secondary-color);
    margin-right: 10px;
    font-size: 1.8rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
    
    i {
      font-size: 1.5rem;
    }
  }
`;

const AnalysisCount = styled.p`
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 20px;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 15px;
  }
`;

const PlanPrice = styled.div`
  background-color: rgba(255, 64, 192, 0.2);
  padding: 10px 20px;
  border-radius: 30px;
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin-bottom: 25px;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    padding: 8px 15px;
    margin-bottom: 15px;
  }
`;

// Styled component for the loading spinner
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 15px;
  z-index: 3;
`;

const LoadingSpinner = styled.div`
  color: var(--secondary-color);
  font-size: 1.8rem;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const PurchaseButton = styled.button`
  background: linear-gradient(45deg, var(--primary-color) 0%, #ff65d5 100%);
  border: none;
  color: white;
  padding: 12px 25px;
  border-radius: 30px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  box-shadow: 0 5px 15px rgba(255, 64, 192, 0.3);
  position: relative;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(255, 64, 192, 0.5);
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const ModalSubtitle = styled.p`
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  margin-bottom: 20px;
  line-height: 1.5;
  font-size: 1.1rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const NoteText = styled.p`
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
  margin-top: 25px;
  font-size: 0.9rem;
  font-style: italic;
`;

// New styled component for the popular badge
const PopularBadge = styled.div`
  position: absolute;
  top: -10px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(45deg, #FFD000, #FFA500);
  color: #170032;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 6px 15px;
  border-radius: 20px;
  z-index: 2;
  box-shadow: 0 4px 12px rgba(255, 208, 0, 0.4), 0 0 0 2px rgba(255, 208, 0, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  white-space: nowrap;
  min-width: 150px;
  text-align: center;
  overflow: visible;
  width: auto;
  
  @media (max-width: 768px) {
    font-size: 0.7rem;
    padding: 5px 12px;
    top: -12px;
    min-width: 130px;
  }
`;

// Enhanced plan card for the popular plan
const PopularPlanCard = styled(PlanCard)`
  border: 2px solid var(--secondary-color);
  transform: scale(1.05);
  box-shadow: 0 10px 25px rgba(255, 208, 0, 0.25);
  position: relative;
  overflow: visible;
  margin-top: 10px;
  
  &::before {
    height: 7px;
    background: var(--secondary-color);
  }
  
  &:hover {
    transform: translateY(-5px) scale(1.05);
    box-shadow: 0 15px 35px rgba(255, 208, 0, 0.35);
    border-color: var(--secondary-color);
  }
  
  @media (max-width: 768px) {
    transform: scale(1.02);
    margin-top: 15px;
    padding-top: 20px;
    
    &:hover {
      transform: translateY(-5px) scale(1.02);
    }
  }
`;

const CreditsPlanModal = ({ onClose }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const { fetchUser } = useAuth();

  // Blocca lo scroll del body quando la modale è aperta
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    
    // Assicurati che la finestra si scrolli all'inizio
    window.scrollTo(0, 0);
    
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  // Chiude la modale quando si preme ESC
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [onClose]);
  
  // Impedisci che il click sul modal si propaghi all'overlay
  const handleModalClick = (e) => {
    e.stopPropagation();
  };

  // Handle purchase action with Stripe integration
  const handlePurchase = async (plan) => {
    try {
      setIsProcessing(true);
      setProcessingPlan(plan);
      
      console.log(`Creating checkout session for plan: ${plan}`);
      
      // Call the Stripe service to create a checkout session
      const { url } = await createCheckoutSession(plan);
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error(`Error during checkout for plan ${plan}:`, error);
      setIsProcessing(false);
      setProcessingPlan(null);
      
      // Show error to user (could be improved with a proper error component)
      alert('Failed to process payment. Please try again later.');
    }
  };

  // Utilizziamo ReactDOM.createPortal per renderizzare il modal direttamente nel nodo root del DOM
  return ReactDOM.createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={handleModalClick}>
        <CloseButton onClick={onClose}>&times;</CloseButton>
        <ModalTitle>Choose Your Credits Plan</ModalTitle>
        <ModalSubtitle>
          Select the plan that works best for you and start analyzing Instagram profiles!
        </ModalSubtitle>
        
        <PlansContainer>
          <PlanCard>
            {isProcessing && processingPlan === 'basic' && (
              <LoadingOverlay>
                <LoadingSpinner>
                  <i className="fas fa-spinner fa-spin"></i>
                </LoadingSpinner>
              </LoadingOverlay>
            )}
            <PlanTitle>Basic Plan</PlanTitle>
            <CreditsAmount><i className="fas fa-coins"></i>500</CreditsAmount>
            <AnalysisCount>2 analyses</AnalysisCount>
            <PlanPrice><span>$</span>4.99</PlanPrice>
            <PurchaseButton 
              onClick={() => handlePurchase('basic')}
              disabled={isProcessing}
            >
              {isProcessing && processingPlan === 'basic' ? 'Processing...' : 'Purchase'}
            </PurchaseButton>
          </PlanCard>
          
          <PopularPlanCard>
            {isProcessing && processingPlan === 'standard' && (
              <LoadingOverlay>
                <LoadingSpinner>
                  <i className="fas fa-spinner fa-spin"></i>
                </LoadingSpinner>
              </LoadingOverlay>
            )}
            <PopularBadge>Most Popular</PopularBadge>
            <PlanTitle>Standard Plan</PlanTitle>
            <CreditsAmount><i className="fas fa-coins"></i>1500</CreditsAmount>
            <AnalysisCount>6 analyses</AnalysisCount>
            <PlanPrice><span>$</span>9.99</PlanPrice>
            <PurchaseButton 
              onClick={() => handlePurchase('standard')}
              disabled={isProcessing}
            >
              {isProcessing && processingPlan === 'standard' ? 'Processing...' : 'Purchase'}
            </PurchaseButton>
          </PopularPlanCard>
          
          <PlanCard>
            {isProcessing && processingPlan === 'premium' && (
              <LoadingOverlay>
                <LoadingSpinner>
                  <i className="fas fa-spinner fa-spin"></i>
                </LoadingSpinner>
              </LoadingOverlay>
            )}
            <PlanTitle>Premium Plan</PlanTitle>
            <CreditsAmount><i className="fas fa-coins"></i>3000</CreditsAmount>
            <AnalysisCount>12 analyses</AnalysisCount>
            <PlanPrice><span>$</span>14.99</PlanPrice>
            <PurchaseButton 
              onClick={() => handlePurchase('premium')}
              disabled={isProcessing}
            >
              {isProcessing && processingPlan === 'premium' ? 'Processing...' : 'Purchase'}
            </PurchaseButton>
          </PlanCard>
        </PlansContainer>
        
        <NoteText>
          Payment will be processed securely via Stripe. Credits will be added to your account immediately after purchase.
        </NoteText>
      </ModalContent>
    </ModalOverlay>,
    document.body // Rendiamo il modal direttamente nel body del documento
  );
};

export default CreditsPlanModal; 